import React, { useCallback, useContext, useEffect, useState } from 'react';
import { NotificationOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { NotificationType } from '../../../models';
import { UserContext } from '../../contexts';
import { NotificationEditPage } from './notification-type-edit-page';
import { EntityChangeLog } from '../../reports';
import { errorTypes } from '../../../models/enums/error-type';

export function NotificationTypesPage() {
  const { Content } = Layout;
  const { Text } = Typography;
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationType[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalNotification, setEditModalNotification] = useState<NotificationType>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'notifications.edit'));
  }, [user]);

  const loadNotifications = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`notifications`)
      .then((response: any) => {
        setNotifications(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const onRefreshClick = () => {
    loadNotifications();
  };

  const onNotificationEditSaved = () => {
    closeEditModal();
    loadNotifications();
  };

  const openEditModal = () => {
    setEditModalNotification(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByNotification = (notification: NotificationType) => {
    setEditModalNotification(notification);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalNotification(undefined);
  };

  const openDetailModalByNotification = (notification: NotificationType) => {
    openEditModalByNotification(notification);
  };

  const deleteNotification = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`notifications/${id}`)
      .then(() => {
        setLoading(false);
        loadNotifications();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteNotification = (id: string) => {
    const onOk = () => deleteNotification(id);
    Modal.confirm({
      title: 'Bildirim türü sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Bildirim türü silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const notification = notifications?.find(m => m.id === id);
      if (notification) {
        openEditModalByNotification(notification);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteNotification(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, notificationType: NotificationType) => (
        <Button type="link" onClick={() => openDetailModalByNotification(notificationType)}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Öncelik puanı',
      dataIndex: 'priorityScore',
      key: 'priorityScore',
    },
    {
      title: 'Hata türü',
      dataIndex: 'errorType',
      key: 'errorType',
      render: (type: string) => errorTypes.get(type),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, notificationType: NotificationType) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(notificationType.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <NotificationOutlined /> Bildirim türleri
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni bildirim türü
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={notifications} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'NotificationType'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Bildirim türü" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <NotificationEditPage notification={editModalNotification} onCancel={closeEditModal} onSaved={onNotificationEditSaved} />
      </Modal>
    </>
  );
}
